import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import theme from '../../../styles/theme';

const HeroSection = ({ onLoginClick }) => {
  const { t } = useTranslation();

  const s = {
    section: {
      background: `linear-gradient(160deg, ${theme.colors.primary.dark} 0%, ${theme.colors.primary.main} 60%, ${theme.colors.secondary.main} 100%)`,
      color: '#fff',
      padding: `${theme.spacing['3xl']} ${theme.spacing.xl}`,
      textAlign: 'center',
    },
    inner: {
      maxWidth: '760px',
      margin: '0 auto',
    },
    eyebrow: {
      display: 'inline-block',
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: theme.colors.accent.light,
      backgroundColor: 'rgba(244,148,95,0.15)',
      padding: `${theme.spacing.xs} ${theme.spacing.md}`,
      borderRadius: theme.borderRadius.full,
      marginBottom: theme.spacing.lg,
    },
    h1: {
      fontSize: theme.typography.fluid.display,
      fontWeight: theme.typography.fontWeight.bold,
      fontFamily: theme.typography.fontFamily.heading,
      lineHeight: theme.typography.lineHeight.tight,
      marginBottom: theme.spacing.lg,
      color: '#fff',
    },
    highlight: {
      color: theme.colors.accent.main,
    },
    sub: {
      fontSize: theme.typography.fontSize.lg,
      lineHeight: theme.typography.lineHeight.relaxed,
      color: 'rgba(255,255,255,0.82)',
      marginBottom: theme.spacing['2xl'],
      maxWidth: '580px',
      margin: `0 auto ${theme.spacing['2xl']}`,
    },
    actions: {
      display: 'flex',
      gap: theme.spacing.md,
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    primaryBtn: {
      backgroundColor: theme.colors.accent.main,
      color: theme.colors.accent.onMain,
      border: 'none',
      borderRadius: theme.borderRadius.full,
      padding: `${theme.spacing.md} ${theme.spacing['2xl']}`,
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.semibold,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-block',
      transition: `background-color ${theme.transitions.fast}`,
    },
    secondaryBtn: {
      backgroundColor: 'rgba(255,255,255,0.12)',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.4)',
      borderRadius: theme.borderRadius.full,
      padding: `${theme.spacing.md} ${theme.spacing['2xl']}`,
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.medium,
      cursor: 'pointer',
      transition: `background-color ${theme.transitions.fast}`,
    },
    mockup: {
      marginTop: theme.spacing['3xl'],
      backgroundColor: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: theme.borderRadius.xl,
      padding: theme.spacing.xl,
      maxWidth: '900px',
      margin: `${theme.spacing['3xl']} auto 0`,
    },
    mockupBar: {
      display: 'flex',
      gap: theme.spacing.sm,
      marginBottom: theme.spacing.lg,
    },
    dot: (color) => ({
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: color,
    }),
    mockupContent: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: theme.spacing.md,
    },
    mockupCard: {
      backgroundColor: 'rgba(255,255,255,0.06)',
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.lg,
      textAlign: 'left',
    },
    mockupLabel: {
      fontSize: theme.typography.fontSize.xs,
      color: 'rgba(255,255,255,0.5)',
      marginBottom: theme.spacing.xs,
      display: 'block',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
    mockupValue: {
      fontSize: theme.typography.fontSize['2xl'],
      fontWeight: theme.typography.fontWeight.bold,
      color: '#fff',
    },
  };

  return (
    <section style={s.section}>
      <div style={s.inner}>
        <span style={s.eyebrow}>{t('public.hero.eyebrow', 'Multi-tenant ERP for pet stores')}</span>
        <h1 style={s.h1}>
          {t('public.hero.headlinePre', 'Run your pet store,')}{' '}
          <span style={s.highlight}>{t('public.hero.headlineHighlight', 'not spreadsheets')}</span>
        </h1>
        <p style={s.sub}>
          {t('public.hero.sub', 'Inventory, sales, pets, daycare and grooming — all in one place. Built for pet product retailers, designed to grow with you.')}
        </p>
        <div style={s.actions}>
          <Link
            to="/register/start?plan=growth"
            style={s.primaryBtn}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = theme.colors.accent.hoverAccessible)}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = theme.colors.accent.main)}
          >
            {t('public.hero.cta', 'Request a demo')}
          </Link>
          <button
            style={s.secondaryBtn}
            onClick={onLoginClick}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
          >
            {t('public.hero.login', 'Log in')}
          </button>
        </div>
      </div>

      {/* App mockup */}
      <div style={s.mockup} className="hero-mockup">
        <div style={s.mockupBar}>
          <div style={s.dot('#FF5F57')} />
          <div style={s.dot('#FEBC2E')} />
          <div style={s.dot('#28C840')} />
        </div>
        <div style={s.mockupContent} className="hero-mockup-grid">
          {[
            { label: t('public.hero.stat1Label', 'Products in stock'), value: '1,248' },
            { label: t('public.hero.stat2Label', 'Orders this month'), value: '342' },
            { label: t('public.hero.stat3Label', 'Active customers'), value: '89' },
          ].map(({ label, value }) => (
            <div key={label} style={s.mockupCard}>
              <span style={s.mockupLabel}>{label}</span>
              <span style={s.mockupValue}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 600px) {
          .hero-mockup-grid { grid-template-columns: 1fr !important; }
          .hero-mockup { padding: 16px !important; }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
